import { useState } from "react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api-client";
import { sanitizeCoachInputOrEmpty } from "@/lib/coach-input";
import type { components } from "@/types/api.generated";
import { useCurrentUser } from "@/hooks/use-current-user";
import type { KrTypeInput } from "@/hooks/use-coach";
import {
  MAX_KRS,
  MIN_KRS,
  type WizardKrDraft,
  type WizardState,
} from "./wizard-state";

type CreateObjectiveBody = components["schemas"]["CreateObjectiveRequest"];
type CreateKrBody = components["schemas"]["CreateKeyResultRequest"];

export interface SaveResult {
  objectiveId: string;
}

export interface ValidatedKr {
  title: string;
  type: KrTypeInput;
  targetValue: number | null;
  quarter: "Q1" | "Q2" | "Q3" | "Q4";
  dueDate: string | null;
}

export function validateWizardForSave(state: WizardState): string | null {
  if (!state.setup.level || !state.setup.type) {
    return "Please complete setup (level and type are required).";
  }
  const statement = state.objective.statement.trim();
  if (!statement) return "Please write an objective statement.";

  const krs = state.keyResults;
  if (krs.length < MIN_KRS) return `Add at least ${MIN_KRS} Key Results.`;
  if (krs.length > MAX_KRS) return `Remove extra Key Results — max is ${MAX_KRS}.`;

  for (let i = 0; i < krs.length; i++) {
    const err = validateKr(krs[i]!, i);
    if (err) return err;
  }
  return null;
}

function validateKr(kr: WizardKrDraft, index: number): string | null {
  const label = `Key Result ${index + 1}`;
  if (!kr.title.trim()) return `${label}: title is required.`;
  if (!["percentage", "binary", "metric"].includes(kr.type)) {
    return `${label}: choose a valid type.`;
  }
  if (kr.type === "metric") {
    const n = Number(kr.targetValue);
    if (!Number.isFinite(n) || n <= 0) {
      return `${label}: target value must be a positive number.`;
    }
  }
  if (!["Q1", "Q2", "Q3", "Q4"].includes(kr.quarter)) {
    return `${label}: choose a quarter.`;
  }
  return null;
}

const sanitizeTitle = (raw: string) => sanitizeCoachInputOrEmpty(raw, 500);

function buildObjectiveBody(state: WizardState, ownerId: string): CreateObjectiveBody {
  return {
    owner_id: ownerId,
    title: sanitizeTitle(state.objective.statement).slice(0, 500),
    description: "",
    type: state.setup.type!,
    level: state.setup.level!,
    year: state.setup.year,
    tactic_ids: state.setup.tacticId ? [state.setup.tacticId] : [],
    focus_area_ids: state.setup.focusAreaId ? [state.setup.focusAreaId] : [],
    parent_objective_ids: state.setup.parentObjectiveId ? [state.setup.parentObjectiveId] : [],
  };
}

interface KrFailure {
  index: number;
  message: string;
}

async function saveKeyResults(
  objectiveId: string,
  krs: WizardKrDraft[],
  ownerId: string,
): Promise<KrFailure[]> {
  const failures: KrFailure[] = [];
  for (let i = 0; i < krs.length; i++) {
    const kr = krs[i]!;
    const body: CreateKrBody = {
      objective_id: objectiveId,
      owner_id: ownerId,
      title: sanitizeTitle(kr.title).slice(0, 500),
      description: "",
      type: kr.type,
      target_value: kr.type === "metric" ? Number(kr.targetValue) : null,
      quarter: kr.quarter,
      due_date: kr.dueDate || null,
    };
    const { error: krErr } = await apiClient.POST("/v1/key-results", { body });
    if (krErr) {
      failures.push({
        index: i + 1,
        message: krErr.error?.message ?? "Unknown error",
      });
    }
  }
  return failures;
}

export function useSaveWizard() {
  const { currentUser } = useCurrentUser();
  const [isSaving, setIsSaving] = useState(false);

  const save = async (state: WizardState): Promise<SaveResult | null> => {
    if (!currentUser) {
      toast.error("You must be signed in to save.");
      return null;
    }
    const validationError = validateWizardForSave(state);
    if (validationError) {
      toast.error(validationError);
      return null;
    }

    setIsSaving(true);
    try {
      const { data: objResp, error: objErr } = await apiClient.POST(
        "/v1/objectives",
        { body: buildObjectiveBody(state, currentUser.id) },
      );
      if (objErr) {
        toast.error(objErr.error.message || "Failed to create objective.");
        return null;
      }
      const objectiveId = objResp!.data.id;
      const failures = await saveKeyResults(objectiveId, state.keyResults, currentUser.id);

      if (failures.length > 0) {
        const detail = failures
          .map((f) => `KR ${f.index}: ${f.message}`)
          .join("; ");
        toast.error(
          `Objective saved, but ${failures.length} Key Result${failures.length > 1 ? "s" : ""} failed (${detail}). Edit on the detail page.`,
          { duration: 12000 },
        );
      } else {
        toast.success("Draft saved");
      }

      return { objectiveId };
    } catch (e) {
      const message = e instanceof Error ? e.message : "Unexpected error.";
      toast.error(`Save failed: ${message}`);
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  return { save, isSaving };
}
